/**
 * Sales tax building blocks: the `tax` a `shipping_debit` and a shipping
 * `/refund` carry, and `sales_tax_breakdown` on a server-to-server delivery.
 * Every amount is integer cents. Inlined by the importing paths, not
 * registered under `schemas`.
 */

const TAX_JURISDICTION_SCHEMA = {
  type: 'object',
  title: 'SalesTaxJurisdiction',
  required: ['level', 'name', 'rate', 'tax'],
  properties: {
    level: {
      type: 'string',
      enum: ['state', 'county', 'city', 'district', 'other'],
      description: 'The kind of taxing authority.',
      example: 'city',
    },
    name: { type: 'string', description: 'The taxing authority.', example: 'Denver' },
    rate: { type: 'number', description: 'Its rate as a decimal fraction (`0.0515` = 5.15%).', example: 0.0515 },
    tax: { type: 'integer', description: 'The tax owed to it, in cents.', example: 187 },
  },
};

const TAX_FEE_SCHEMA = {
  type: 'object',
  title: 'SalesTaxFee',
  required: ['name', 'type', 'amount'],
  properties: {
    name: { type: 'string', description: 'The authority that levies the fee.', example: 'Colorado' },
    type: { type: 'string', description: 'What the fee is.', example: 'RETAIL_DELIVERY_FEE STATE SALES TAX' },
    amount: { type: 'integer', description: 'The fee, in cents.', example: 31 },
  },
};

const TAX_LINE_SCHEMA = {
  type: 'object',
  title: 'SalesTaxLine',
  required: ['kind', 'amount', 'category', 'tax'],
  properties: {
    kind: {
      type: 'string',
      enum: ['item', 'shipping', 'insurance', 'signature', 'handling'],
      description: 'Which part of the charge: a card, or a part of the delivery fee.',
      example: 'item',
    },
    pool_item_id: { type: 'integer', description: 'The card, on an `item` line only.', example: 7001 },
    amount: { type: 'integer', description: 'The taxed amount, in cents: the card’s taxable value, or the fee.', example: 2500 },
    category: { type: 'string', description: 'The tax category it was rated under.', example: 'GENERAL_MERCHANDISE' },
    tax: { type: 'integer', description: 'Its tax, fees included, in cents.', example: 229 },
  },
};

const TAX_COMMON_PROPERTIES = {
  amount: { type: 'integer', description: 'The sales tax, in cents.', example: 363 },
  state: { type: 'string', description: 'The US state the tax was quoted for.', example: 'CO' },
  calculation_id: { type: 'string', description: 'The tax calculation it came from, for your records.', example: 'calc_9f3a2b' },
  jurisdictions: {
    type: 'array',
    description: 'The tax owed to each jurisdiction. With `fees`, adds up to `amount`.',
    items: TAX_JURISDICTION_SCHEMA,
  },
  fees: {
    type: 'array',
    description: 'Flat fees collected with the tax, such as Colorado’s Retail Delivery Fee; empty where none apply.',
    items: TAX_FEE_SCHEMA,
  },
};

export const SHIPPING_DEBIT_TAX_SCHEMA = {
  type: 'object',
  title: 'ShippingDebitTax',
  required: ['amount', 'state', 'calculation_id', 'lines', 'jurisdictions', 'fees'],
  description:
    'Present only when sales tax applies to this delivery; the key is left out otherwise. `amount` equals `fees.sales_tax`; ' +
    'the `lines` add up to it, and so do the `jurisdictions` and `fees` together. Every amount is integer cents.',
  properties: {
    amount: TAX_COMMON_PROPERTIES.amount,
    state: TAX_COMMON_PROPERTIES.state,
    calculation_id: TAX_COMMON_PROPERTIES.calculation_id,
    lines: { type: 'array', description: 'The tax on each part of the charge. Adds up to `amount`.', items: TAX_LINE_SCHEMA },
    jurisdictions: TAX_COMMON_PROPERTIES.jurisdictions,
    fees: TAX_COMMON_PROPERTIES.fees,
  },
};

export const REFUND_TAX_SCHEMA = {
  type: 'object',
  title: 'RefundTax',
  required: ['amount', 'state', 'calculation_id', 'jurisdictions', 'fees'],
  description:
    'The sales tax inside `amount`, on a shipping refund that returns some; the key is left out otherwise. A jurisdiction ' +
    'or fee with nothing to return is left out of its list.',
  properties: TAX_COMMON_PROPERTIES,
};

// A Denver delivery of one card: 9.15% on the card and the shipping, handling exempt, plus the $0.31 fee.
export const TAX_EXAMPLE = {
  amount: 363,
  state: 'CO',
  calculation_id: 'calc_9f3a2b',
  lines: [
    { kind: 'item', pool_item_id: 7001, amount: 2500, category: 'GENERAL_MERCHANDISE', tax: 229 },
    { kind: 'shipping', amount: 1122, category: 'SHIPPING', tax: 134 },
    { kind: 'handling', amount: 320, category: 'HANDLING', tax: 0 },
  ],
  jurisdictions: [
    { level: 'state', name: 'Colorado', rate: 0.029, tax: 105 },
    { level: 'city', name: 'Denver', rate: 0.0515, tax: 187 },
    { level: 'district', name: 'Colorado Regional Transportation District', rate: 0.01, tax: 36 },
    { level: 'district', name: 'Colorado Scientific and Cultural Facilities District', rate: 0.001, tax: 4 },
  ],
  fees: [{ name: 'Colorado', type: 'RETAIL_DELIVERY_FEE STATE SALES TAX', amount: 31 }],
};
